import { STATUS_LABEL, STEP_LABEL, STEP_ORDER } from '../lib/vocabulary.ts'
import { useEffect, useMemo, useState } from 'react'
import type { TimelineEntry } from '../lib/api.ts'
import { humanDuration, stepDurations } from '../lib/timeline.ts'
import { Tooltip } from './Tooltip.tsx'

/**
 * What the analysis is doing, as checkpoints rather than a spinner.
 *
 * A simulation is watched: somebody uploads a prescription and stays on the
 * screen. A spinner tells them nothing about whether we are reading, waiting
 * for a slot, or stuck -- and `waiting` in particular is a deliberate pause we
 * should name rather than disguise as work.
 *
 * Drawn along a line, in the same language as the guided pass's rail: dots on
 * a rule, filled behind you, hollow ahead.
 *
 * **It moves.** The checkpoints are minutes apart -- reading a creased page is
 * the better part of one -- so a strip that only ever changed between polls
 * looked identical to a strip that had stopped. The current dot pulses, the
 * line grows into the next one, and the heading trails dots, so "still
 * working" is visible without waiting for the next checkpoint to prove it.
 */
/** The statuses of a run with no checkpoint yet: queued, or about to start again. */
const NOT_STARTED: ReadonlySet<string> = new Set([
  '',
  'waiting',
  'reverifying',
  'reading',
  'evaluating',
])

export function Progress({
  step,
  /** What is going on, before the checkpoint's own name. */
  action = 'Analisando',
  /**
   * The queue's size.
   *
   * Same rail, smaller, and without a heading -- the row's status cell already
   * names the checkpoint, and repeating it under itself would spend the one
   * line a table row can spare. Fifty of these sit on screen at once, so the
   * dots shrink and the pulse stays: at this size the beat is most of what
   * says the row is moving at all.
   */
  compact = false,
  timeline,
}: {
  step: string
  action?: string
  compact?: boolean
  /** Every checkpoint with when it began; each dot gets its duration. */
  timeline?: readonly TimelineEntry[] | undefined
}) {
  /*
   * Re-rendered once a second while a checkpoint is live, so the number under
   * it keeps counting. Nothing else on this strip changes on its own.
   */
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    if (compact || timeline === undefined) return undefined
    const tick = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(tick)
  }, [compact, timeline])
  const durations = useMemo(() => stepDurations(timeline, now), [timeline, now])
  const checkpoint = STEP_ORDER.indexOf(step as (typeof STEP_ORDER)[number])
  /*
   * A run that has not reached its first checkpoint yet.
   *
   * The pages hand over the status when there is no checkpoint -- `waiting`
   * while a run is queued, or while an orçamento has nothing attached yet --
   * and this printed it as the raw key. It is the start of the same strip:
   * nothing done, the first dot beating.
   */
  const starting = checkpoint === -1 && NOT_STARTED.has(step)
  const current = starting ? 0 : checkpoint

  // Anything else we cannot place -- a failure, or a step added later -- is
  // said in words, never as its key.
  if (current === -1) {
    return (
      <p className="text-tP text-text-weak">
        {STEP_LABEL[step] ?? STATUS_LABEL[step] ?? 'Analisando…'}
      </p>
    )
  }

  const progress = starting ? 0 : (current / (STEP_ORDER.length - 1)) * 100
  const dot = compact ? 'h-2 w-2' : 'h-3 w-3'

  return (
    <div className={`flex flex-col ${compact ? 'gap-1' : 'gap-2'}`}>
      {!compact && (
        <span className="text-tP text-text-strong flex items-baseline gap-1.5">
          {starting ? (
            <span className="font-semibold">Aguardando o início</span>
          ) : (
            <>
              <span className="text-text-weak">{action}</span>
              <span className="font-semibold">{STEP_LABEL[step] ?? step}</span>
            </>
          )}
          <Ellipsis />
        </span>
      )}

      <ol
        className={`relative flex items-start ${compact ? 'h-2' : 'h-4'}`}
        aria-label="Progresso da análise"
      >
        {/* The rule the dots sit on, behind them; each dot has its own fill so
            it punches through. */}
        <span
          aria-hidden="true"
          className={`bg-stroke-weak absolute h-0.5 ${
            compact ? 'left-1 right-1 top-[3px]' : 'left-1.5 right-1.5 top-1.5'
          }`}
        />
        {/* How far along, growing into the next checkpoint rather than
            jumping. Slow enough to read as travel. */}
        <span
          aria-hidden="true"
          className={`bg-fill-brand absolute h-0.5 transition-[width] duration-700 ease-out motion-reduce:transition-none ${
            compact ? 'left-1 top-[3px]' : 'left-1.5 top-1.5'
          }`}
          style={{ width: `calc(${progress}% - ${compact ? 8 : 12}px)` }}
        />

        {STEP_ORDER.map((step, index) => {
          const done = !starting && index < current
          const active = index === current
          const label = STEP_LABEL[step] ?? step

          return (
            <li
              key={step}
              aria-current={active ? 'step' : undefined}
              className="relative flex flex-1 justify-start last:flex-none"
            >
              <span className="sr-only">{label}</span>

              {/*
                The dot is the hover target, not the whole segment. A segment
                stretches most of the way to the next checkpoint, so hovering
                anywhere in the gap would name a dot the pointer is nowhere
                near.
              */}
              {/*
                The product's Tooltip, not a span positioned above the dot.
                
                The hand-made one was `absolute` with a z-index, which cannot
                escape the sticky table header the queue draws over it -- so
                the checkpoint's name appeared underneath the column headings
                and could not be read. Ours renders into the top layer, where
                nothing can be in front of it.
              */}
              <Tooltip text={label}>
                <span className={`group relative block ${dot}`}>
                  {/*
                  The beat. Only on the checkpoint being worked, and dropped
                  when the reader has asked for less motion -- a strip pulsing
                  forever in the corner of somebody's eye while they read a
                  prescription is its own kind of noise.
                */}
                  {active && (
                    <span
                      aria-hidden="true"
                      className="bg-fill-brand absolute inset-0 animate-ping rounded-full opacity-50 motion-reduce:hidden"
                    />
                  )}
                  <span
                    aria-hidden="true"
                    className={`relative block ${dot} rounded-full border-2 transition-colors duration-500 motion-reduce:transition-none ${
                      done
                        ? 'bg-fill-brand border-stroke-brand-strong'
                        : active
                          ? 'bg-fill-white border-stroke-brand-strong'
                          : 'bg-fill-white border-stroke-weak'
                    }`}
                  />
                </span>
              </Tooltip>
            </li>
          )
        })}
      </ol>

      {/*
        Where the time went, under each dot. This is the whole reason the
        timeline exists: "why is it slow" is answered by reading the biggest
        number, not the worker's logs.
      */}
      {!compact && durations.size > 0 && (
        <ol className="flex items-start" aria-label="Duração de cada etapa">
          {STEP_ORDER.map((step) => {
            const duration = durations.get(step)
            return (
              <li
                key={step}
                className="text-tTiny text-text-weak flex-1 tabular-nums last:flex-none"
              >
                {duration === undefined
                  ? ''
                  : humanDuration(duration.ms) + (duration.live ? '…' : '')}
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

/**
 * Three dots, arriving one at a time.
 *
 * The cheapest possible proof of life, and the only part of the strip that
 * moves every second rather than every few minutes. Static under
 * `prefers-reduced-motion`, where it becomes an ordinary ellipsis.
 */
function Ellipsis() {
  return (
    <span aria-hidden="true" className="text-text-weak inline-flex">
      {[0, 200, 400].map((delay) => (
        <span
          key={delay}
          className="animate-pulse motion-reduce:animate-none"
          style={{ animationDelay: `${delay}ms`, animationDuration: '1200ms' }}
        >
          .
        </span>
      ))}
    </span>
  )
}
